import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * El gasto excede el presupuesto y la empresa bloquea (409 PRESUPUESTO_EXCEDIDO): el detalle por
 * cuenta y, si el usuario puede autorizar excesos, el motivo para guardarlo igual. Lo usan la
 * orden de compra y el asiento manual (P9).
 *
 * `que`: «La orden» o «El asiento». `onAutorizar(motivo)` vuelve a guardar con el motivo.
 */
export default function ModalExcedido({ excedido, que = 'La orden', volver = 'Volver a la orden', columna = 'Esta orden', saving, onCerrar, onAutorizar }) {
  const [motivo, setMotivo] = useState('');
  if (!excedido) return null;
  const p = excedido.controlPresupuesto?.presupuesto;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCerrar}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-prueba="presupuesto-excedido">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2"><AlertTriangle size={20} className="text-red-600" /> {que} excede el presupuesto</h3>
          <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <p className="text-gray-700">{excedido.error}.</p>
          {p && <p className="text-xs text-gray-500" data-prueba="exceso-contra">Contra el presupuesto {p.anio} · {p.escenario}{p.periodo ? `, período ${p.periodo}` : ''}, en {p.moneda}.</p>}
          <table className="w-full text-xs">
            <thead className="text-gray-500"><tr><th className="text-left py-1">Cuenta</th><th className="text-right py-1">Presupuesto</th><th className="text-right py-1">Real</th><th className="text-right py-1">Comprometido</th><th className="text-right py-1">Disponible</th><th className="text-right py-1">{columna}</th></tr></thead>
            <tbody className="font-mono">
              {(excedido.controlPresupuesto?.detalles || []).map((d, i) => (
                <tr key={i} className={d.excede ? 'text-red-700 font-semibold' : 'text-gray-700'}>
                  <td className="py-1 font-sans">{d.cuenta || 'Centro de costo'}</td>
                  {['presupuesto', 'real', 'comprometido', 'disponible', 'solicitado'].map(k => <td key={k} className="py-1 text-right">{Number(d[k]).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {(excedido.controlPresupuesto?.mensajes || []).filter(m => /sin cuenta/.test(m)).map(m => <p key={m} className="text-amber-700">{m}</p>)}
          {excedido.puedeExceder ? (
            <div className="space-y-2">
              <label className="block font-medium text-gray-700">Motivo para autorizar el exceso *</label>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Queda registrado en la bitácora del control del gasto" />
            </div>
          ) : (
            <p className="text-gray-600">Para guardarlo, ajuste los montos o pida a alguien con permiso de autorizar excesos de presupuesto.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <button onClick={onCerrar} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">{volver}</button>
          {excedido.puedeExceder && <button disabled={saving || !motivo.trim()} onClick={() => onAutorizar(motivo.trim())} className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50">Autorizar y guardar</button>}
        </div>
      </div>
    </div>
  );
}
